import React, { useState, useContext } from 'react'
import { compose } from 'recompose'
import { useDispatch, useSelector } from 'react-redux'
import PlusLoadModalActionCreatorsMapping from 'State/Store/PlusLoadModal/PlusLoadModalActionCreatorsMapping'
import createPlusLoadModalSelector from 'State/Store/PlusLoadModal/createPlusLoadModalSelector'
import ModalComponent from 'Components/Modal/Modal.component'
import { Grid, Button, FormControl, withStyles, Typography, FormHelperText } from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import themeCreateInjectableStyles from 'Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support'
import plusLoadModalStyle from './PlusLoadModal.style'
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import PleaseWatiSpinner from './comps/PleaseWatiSpinner'
import ProgressDone from './comps/ProgressDone'
import hitGTM from 'State/Store/UI/Actions/GTM.action'
import { UI_PLUS_LOAD_FORM_DIALOG_NEXT_BTN, UI_PLUS_LOAD_FORM_DIALOG_CANCEL, UI_PLUS_LOAD_FORM_DIALOG_CALL_BORAL_LINK, UI_PLUS_LOAD_CONFIRMATION_DIALOG_SUBMIT_BTN } from 'State/Store/UI/UI.types'
import { Order } from 'types/Order'
import { callBoralTel } from 'utils/call-boral-tel'
import { openSnackbar, SNACKBAR_GENERAL_ERROR_MESSAGE } from 'State/Store/Snackbar/SnackbarActionCreators'
import ServicesContext from 'Contexts/Services/Services.context'
import { getPlusLoadInitialFooter, getPlusLoadInitialTitle, getPlusLoadInitialContent } from './parts/get-initial-parts'
import { getPlusLoadConfirmTitle, getPlusLoadConfirmContent, getPlusLoadConfirmFooter } from './parts/get-confirm-parts'

interface Props {
  order: Order,
  onGoToThisOrder(): void,
  onGoToOrders(): void,
  onCloseWhenDone(): void,
}

interface InjectedProps {
  classes: any,
  width: any,
}

enum Status {
  INITIAL,
  CONFIRM,
  PROCESSING,
  DONE,
}

const PlusLoadModalComp = (props: Props & InjectedProps) => {
  const {
    order,
    onGoToThisOrder,
    onGoToOrders,
    onCloseWhenDone,
    width,
    classes = {},
  } = props
  const dispatch = useDispatch()
  const services = useContext(ServicesContext)
  const plusLoadModalStore = useSelector(createPlusLoadModalSelector)
  const { open } = plusLoadModalStore
  const [ status, setStatus ] = useState<Status>(Status.INITIAL)
  const currentOrderQuantity = order.details ? order.details.qty.ordered : 0
  const [ quantity, setQuantity ] = useState<number | undefined>(undefined)
  const isQuantityValid = typeof quantity === 'number' && quantity >= 0.1 && quantity <= 7.0
  if (!open) return null

  const handleClose = () => {
    if (status === Status.DONE) {
      onCloseWhenDone()
    }
    dispatch(hitGTM(UI_PLUS_LOAD_FORM_DIALOG_CANCEL))
    setStatus(Status.INITIAL)
    setQuantity(undefined)
    dispatch(PlusLoadModalActionCreatorsMapping.closePlusLoadModal())
  }
  const handleCallBoral = () => {
    dispatch(hitGTM(UI_PLUS_LOAD_FORM_DIALOG_CALL_BORAL_LINK))
    callBoralTel(order.details!.boralTel)
  }
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueString: string = e.target.value
    if (valueString) {
      setQuantity(Number(valueString))
    } else {
      setQuantity(undefined)
    }
  }
  // INITIAL -> CONFIRM
  const handleNext = () => {
    dispatch(hitGTM(UI_PLUS_LOAD_FORM_DIALOG_NEXT_BTN))
    setStatus(Status.CONFIRM)
  }
  // CONFIRM -> INITIAL
  const handleBack = () => {
    setStatus(Status.INITIAL)
  }
  const handleTitleIconClick = () => {
    if (status === Status.CONFIRM) {
      handleBack()
    }
  }
  const handleSubmit = async () => {
    dispatch(hitGTM(UI_PLUS_LOAD_CONFIRMATION_DIALOG_SUBMIT_BTN))
    try {
      setStatus(Status.PROCESSING)
      await services.orderService.submitPlusLoad({ orderKey: order.orderKey, quantity: quantity! })
      setStatus(Status.DONE)
    } catch (ex) {
      dispatch(openSnackbar({ message: SNACKBAR_GENERAL_ERROR_MESSAGE }))
      setStatus(Status.INITIAL)
    }
  }

  const getParts = (): {
    title: null | React.ReactNode,
    content: null | React.ReactNode,
    footer: null | React.ReactNode,
    titleIcon?: null | React.ReactNode,
  } => {
    switch (status) {
      case Status.INITIAL:
        return getInitialParts()
      case Status.CONFIRM:
        return getConfirmParts()
      case Status.PROCESSING:
        return getProgressingParts()
      case Status.DONE:
        return getDoneParts()
      default:
        return {
          title: null,
          content: null,
          footer: null,
          titleIcon: null,
        }
    }
  }

  const getInitialParts = () => {
    const title = getPlusLoadInitialTitle()
    const content = getPlusLoadInitialContent({ phoneNumber: !isWidthDown('sm', width) && order && order.details && order.details.boralTel ? order.details.boralTel : null, classes, onCallBoral: handleCallBoral, quantity, onInputChange: handleInputChange })
    const footer = getPlusLoadInitialFooter({ classes, onCancel: handleClose, onNext: handleNext, isQuantityValid })
    return { title, content, footer }
  }

  const getConfirmParts = () => {
    const title = getPlusLoadConfirmTitle()
    const content = quantity === undefined ? null : getPlusLoadConfirmContent({ classes, quantity, currentOrderQuantity })
    const footer = getPlusLoadConfirmFooter({ classes, isQuantityValid, onBack: handleBack, onSubmit: handleSubmit })
    return { title, content, footer, titleIcon: <ArrowBackIcon /> }
  }

  const getProgressingParts = () => {
    return {
      title: null,
      content: <PleaseWatiSpinner />,
      footer: null,
    }
  }

  const getDoneParts = () => {
    return {
      title: ' ',
      content: <ProgressDone
        title={`Your plus load has been submitted`}
        stepContents={[
          `Your plus load will be batched and delivered asap or as scheduled.`,
          `You can continue to track your order in Boral Connects.`,
          `We will call you if there's a problem.`,
        ]}
      />,
      footer: (
        <Grid className={classes.textAlignRight} container justify='flex-end' spacing={2}>
          <Grid className={classes.flexGrow1} item>
            <Button
              className={classes.button}
              onClick={() => {
                dispatch(PlusLoadModalActionCreatorsMapping.closePlusLoadModal())
                onGoToOrders()
              }}
              variant='contained'
            >
              { `Go to all orders` }
            </Button>
          </Grid>
          <Grid item>
            <Button
              className={classes.button}
              color='primary'
              onClick={() => {
                dispatch(PlusLoadModalActionCreatorsMapping.closePlusLoadModal())
                onGoToThisOrder()
              }}
              variant='contained'
            >
              { `Go to this order` }
            </Button>
          </Grid>
        </Grid>
      ),
    }
  }
  const { title, content, footer, titleIcon = null } = getParts()
  return (
    <ModalComponent
      maxWidth='sm'
      fullWidth
      open={true}
      onClose={handleClose}
      rightHeading={null}
      dialogContentClasses={classes.modalContent}
      dialogTitleRootClasses={
        (status === Status.INITIAL || status === Status.CONFIRM) && classes.modalTitleWithDivider
      }
      title={title}
      onIconClick={status !== Status.DONE && handleTitleIconClick}
      icon={titleIcon}
      content={content}
      footer={footer}
    />
  )
}


const PlusLoadModal = compose<Props & InjectedProps, Props>(
  withWidth(),
  withStyles(themeCreateInjectableStyles(plusLoadModalStyle) as any, { withTheme: true }),
)(PlusLoadModalComp)

export default PlusLoadModal
